import { TFlat } from "../../../../shared/types/flat";

/**
 * Updates the hovered Flat SVG element adding or removing the 'hovered' class.
 * @param {Document | null | undefined} svgDocument - The SVG document containing the Flat elements.
 * @param {string | undefined} hoveredFlatId - The ID of the flat to set as hovered (Flat name).
 * @param {(flatName: string) => void} onElementHover - Callback function to trigger when a flat is hovered.
 * @returns {void}
*/
export const updateHoveredFlat = (svgDocument: Document | null | undefined, hoveredFlatId: string | undefined, onElementHover: (flatName: string) => void): void => {
    if (!svgDocument) {
        return;
    }

    const flatsElements = svgDocument.querySelectorAll('[data-type="flat"]');

    flatsElements.forEach((element) => {
        element.classList.remove('hovered');
    });

    if (hoveredFlatId) {
        const flatElement = svgDocument.querySelector(`[data-type="flat"][id="${hoveredFlatId}"]`);

        const flatId = flatElement?.getAttribute('id');
        flatElement?.classList.add('hovered');
    }
};

/**
 * Updates the color of each Flat SVG element based on its availability status.
 * @param {Document | null | undefined} svgDocument - The SVG document containing the Flat elements.
 * @param {TFlat[]} flats - An array of Flats with their statuses to apply corresponding styles.
 * @returns {void}
*/
export const updateFlatColorByStatuses = (svgDocument: Document | null | undefined, flats: TFlat[]): void => {
    if (!svgDocument) {
        return;
    }

    const flatsElements = svgDocument.querySelectorAll('[data-type="flat"]');

    flatsElements.forEach((element) => {
        const flatName = element.getAttribute('id');

        if (!flatName) {
            return;
        }

        const flat = flats.find((flat: any) => flat.name === flatName);

        element.classList.remove('reservation', 'available', 'sold');

        if (flat) {
            switch (flat.status) {
                case 'rezerwacja':
                    element.classList.add('reservation');
                    break;
                case 'dostepne':
                    element.classList.add('available');
                    break;
                case 'sprzedane':
                    element.classList.add('sold');
                    break;
                default:
                    element.classList.remove('reservation', 'available', 'sold');
                    break;
            }
        }
    });
};

/**
 * Updates the selected Flat SVG element by applying the 'selected' class.
 * Resets all other Flat elements to their default state.
 * @param {NodeListOf<Element>} flatsElements - A NodeList of SVG Flat elements.
 * @param {TFlat} selectedFlat - The Flat object representing the selected Flat.
 * @returns {void}
*/
export const updateFlatColorByChangingSelectedClass = (flatsElements: NodeListOf<Element>, selectedFlat: TFlat): void => {
    flatsElements.forEach((element) => {
        const svgElement = element as SVGElement;
        const flatId = svgElement.getAttribute('id');

        svgElement.classList.remove('selected');

        svgElement.style.fill = '';
        svgElement.style.fillOpacity = '0.01';

        if (svgElement.classList.contains('selected')) {
            return;
        }

        if (selectedFlat.name !== '') {
            if (flatId && flatId.toLowerCase() === selectedFlat.name.toLowerCase()) {
                svgElement.classList.add('selected');

                if (svgElement.classList.contains('selected')) {
                    return;
                }
            }
        }
    });
};

/**
 * Filters out Flats in the SVG by adding the 'filtered-out' class to elements not in the provided list of Flats.
 * @param {TFlat[]} contextFlats - An array of Flats to keep visible in the SVG.
 * @param {NodeListOf<Element>} flatsElements - A NodeList of SVG flat elements.
 * @returns {void}
*/
export const updateFilterOutFlats = (contextFlats: TFlat[], flatsElements: NodeListOf<Element>): void => {
    const flatNames = contextFlats.map(flat => flat.name);
    flatsElements.forEach(element => {
        const flatName = element.getAttribute('id');

        if (flatName && !flatNames.includes(flatName)) {
            element.classList.add('filtered-out');
        } else {
            element.classList.remove('filtered-out');
        }
    });
}